import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useSelector } from "react-redux";
import { AdminHeader } from "../../../components/adminHeader/adminHeader";
import { Button } from "../../../components/button/button";
import { Separator } from "../../../components/separator/separator";
import { MultiSelectList } from "../../../components/multiSelectList/multiSelectList";
import { Text } from "../../../components/text/text";
import { PeriodRangeCalendar } from "../../../components/periodRangeCalendar/periodRangeCalendar";
import AdminAPI from "../../../api/AdminAPI";
import SuperAdminAPI from "../../../api/SuperAdminAPI";
import { downloadFile } from "../../../utils/downloadFile";
import { selectAdminCafes } from "../../../features/user/selectors";
import { useMultiSelect } from "../../../hooks/useMultiSelect";
import styles from "./comparativeRevenueReport.module.css";

export const ComparativeRevenueReportPage = () => {
    const isSuperAdmin = useLocation().pathname.startsWith("/superadmin");
    const adminCafes = useSelector(selectAdminCafes);

    const [superAdminCafes, setSuperAdminCafes] = useState([]);
    const [cafesLoading, setCafesLoading] = useState(false);
    const [cafeError, setCafeError] = useState(false);
    const [periodError, setPeriodError] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const cafes = isSuperAdmin ? superAdminCafes : adminCafes;
    const { selectedIds, selectedIdList, toggle, toggleMany } = useMultiSelect(cafes);

    const now = new Date();
    const defaultEndMonth = now.getMonth() === 0 ? 12 : now.getMonth();
    const defaultEndYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const defaultStartMonth = defaultEndMonth <= 3 ? defaultEndMonth + 9 : defaultEndMonth - 3;
    const defaultStartYear = defaultEndMonth <= 3 ? defaultEndYear - 1 : defaultEndYear;

    const [startMonth, setStartMonth] = useState(defaultStartMonth);
    const [startYear, setStartYear] = useState(defaultStartYear);
    const [endMonth, setEndMonth] = useState(defaultEndMonth);
    const [endYear, setEndYear] = useState(defaultEndYear);

    useEffect(() => {
        if (!isSuperAdmin) return undefined;

        let cancelled = false;
        setCafesLoading(true);
        SuperAdminAPI
            .fetchCafes()
            .then((list) => {
                if (!cancelled) setSuperAdminCafes(list);
            })
            .catch((error) => {
                if (!cancelled) console.error(error);
            })
            .finally(() => {
                if (!cancelled) setCafesLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isSuperAdmin]);

    useEffect(() => {
        if (selectedIds.size > 0) setCafeError(false);
    }, [selectedIds]);

    const handlePeriodChange = ({ startMonth: sm, startYear: sy, endMonth: em, endYear: ey }) => {
        setStartMonth(sm);
        setStartYear(sy);
        setEndMonth(em);
        setEndYear(ey);
        setPeriodError(false);
    };

    const validate = () => {
        let hasError = false;
        if (selectedIdList.length === 0) {
            setCafeError(true);
            hasError = true;
        } else {
            setCafeError(false);
        }

        const startKey = startYear * 12 + startMonth;
        const endKey = endYear * 12 + endMonth;
        if (endKey < startKey || endKey - startKey + 1 > 24) {
            setPeriodError(true);
            hasError = true;
        } else {
            setPeriodError(false);
        }
        return hasError;
    };

    const onDownload = () => {
        if (validate()) return;

        setDownloading(true);
        AdminAPI
            .downloadComparativeRevenueReport({
                cafeIds: selectedIdList,
                startMonth,
                startYear,
                endMonth,
                endYear,
            })
            // Время жизни blob-ссылки держит downloadFile: Safari и WebView
            // дочитывают файл уже после клика.
            .then(({ url, filename }) => downloadFile(url, filename))
            .catch(console.error)
            .finally(() => setDownloading(false));
    };

    return (
        <>
            <AdminHeader title="Сравнительный отчёт по выручке" />

            {cafesLoading ? (
                <Text as="div" variant="body">
                    Загрузка кофеен...
                </Text>
            ) : (
                <MultiSelectList
                    title="Кофейни"
                    items={cafes}
                    selectedIds={selectedIds}
                    onToggle={toggle}
                    onToggleMany={toggleMany}
                    error={cafeError}
                    allLabel="Все кофейни"
                    emptyText="Нет доступных кофеен"
                    notFoundText="Кофейня не найдена"
                />
            )}

            {cafeError && (
                <Text as="div" variant="caption" className={styles.errorText}>
                    Выберите хотя бы одну кофейню
                </Text>
            )}

            <Text as="div" variant="bodyStrong" className={styles.periodTitle}>
                Период отчёта
            </Text>

            <PeriodRangeCalendar
                startMonth={startMonth}
                startYear={startYear}
                endMonth={endMonth}
                endYear={endYear}
                onChange={handlePeriodChange}
                error={periodError}
            />

            {periodError && (
                <Text as="div" variant="caption" className={styles.errorText}>
                    Период должен быть от 1 до 24 месяцев, дата окончания не раньше начала
                </Text>
            )}

            <Separator />

            <div className={styles.pageSpacer} />

            <Button
                fixed
                disabled={downloading || cafesLoading}
                text={downloading ? "Формируем..." : "Скачать отчёт"}
                onClick={onDownload}
            />
        </>
    );
};
